import React from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import get from 'lodash.get';
import { getGoalSection } from '../../../utils/shared/cellUtils';
import { getReadingScore } from '../../../utils/reading/cellUtils';
import CompositeCell from '../shared/CompositeCell';
import SharedScoreCell from '../shared/ScoreCell';
import { openNonD8ProbeDetails, openProbeDetails } from '../../../utils/reading/tableUtils';
import { isBurstMeasure } from '../../../utils/reading/filterUtils';
import {
    MEASURE,
    MEASURES_WITH_PERCENTAGE,
    TRC_OR_TRO,
    getBurstMeasureCodeByKey,
} from '../../../constants/reading/MeasuresConstants';
import { URL } from '../../../constants/reading/ApiConstants';
import { TRO_UNTRANSCRIBABLE_CELL } from '../../../constants/reading/TroConstants';
import './Cells.scss';

const { OVERALL, MAZE } = MEASURE;

const openAnyProbeDetails = (
    measure,
    period,
    statusForMeasureYearPeriod,
    externalId,
    location,
    openPanel,
) => {
    const { isComplete } = statusForMeasureYearPeriod;

    const resultId = get(
        statusForMeasureYearPeriod,
        'resultId',
        get(statusForMeasureYearPeriod, 'id'),
    );

    if (isBurstMeasure(measure) && isComplete) {
        openNonD8ProbeDetails(
            URL.BURST.BURST_PROBE_DETAILS,
            'joule',
            getBurstMeasureCodeByKey(measure),
            period,
            resultId,
            externalId,
            location,
        );
    } else {
        openProbeDetails(statusForMeasureYearPeriod, resultId, openPanel);
    }
};

const CommonCellBuilder = ({
    cellType,
    statusForMeasureYearPeriod,
    measure,
    period,
    openPanel,
    externalId,
    location,
    gatingMeasureCellType,
    optionalCellData,
    sortIndex,
    isDemoModeOn,
    isCellDisabled,
    resetCellData,
}) => {
    const { interp, score, isUntranscribable, percentile, isSecure } = statusForMeasureYearPeriod;
    const { goal, isGoalAvailable } = optionalCellData;
    const goalSection = getGoalSection(goal, isGoalAvailable, measure, MEASURES_WITH_PERCENTAGE);

    const openProbeDetail = () =>
        openAnyProbeDetails(
            measure,
            period,
            statusForMeasureYearPeriod,
            externalId,
            location,
            openPanel,
        );

    if (measure === OVERALL) {
        return (
            <CompositeCell
                interp={interp}
                score={score}
                cellType={cellType}
                optionalCellData={optionalCellData}
                sortIndex={sortIndex}
                goalSection={goalSection}
                percentile={percentile}
                isSecure={isSecure}
                resetCellData={resetCellData}
            />
        );
    }
    return (
        <SharedScoreCell
            cellType={cellType}
            gatingMeasureCellType={gatingMeasureCellType}
            isCellDisabled={isCellDisabled}
            statusForMeasureYearPeriod={statusForMeasureYearPeriod}
            measure={measure}
            onScoreClick={measure === MAZE && isDemoModeOn ? () => {} : openProbeDetail}
            onEmptyScoreClick={
                isEqual(measure, MAZE) && !isDemoModeOn ? () => openPanel() : () => {}
            }
            optionalCellData={optionalCellData}
            sortIndex={sortIndex}
            goalSection={goalSection}
            measuresWithPercentage={MEASURES_WITH_PERCENTAGE}
            measuresNotCompleted={TRC_OR_TRO}
            getCustomScore={props =>
                isUntranscribable ? TRO_UNTRANSCRIBABLE_CELL : getReadingScore(props)
            }
            isSecure={isSecure}
        />
    );
};

CommonCellBuilder.defaultProps = {
    cellType: null,
    statusForMeasureYearPeriod: {},
    measure: null,
    period: null,
    openPanel: () => {},
    externalId: null,
    location: {},
    gatingMeasureCellType: null,
    optionalCellData: {},
    sortIndex: {},
    isCellDisabled: false,
    isDemoModeOn: false,
    resetCellData: {},
};

CommonCellBuilder.propTypes = {
    cellType: PropTypes.string,
    statusForMeasureYearPeriod: PropTypes.object,
    measure: PropTypes.string,
    period: PropTypes.string,
    openPanel: PropTypes.func,
    externalId: PropTypes.string,
    location: PropTypes.object,
    gatingMeasureCellType: PropTypes.string,
    optionalCellData: PropTypes.object,
    sortIndex: PropTypes.object,
    isCellDisabled: PropTypes.bool,
    isDemoModeOn: PropTypes.bool,
    resetCellData: PropTypes.object,
};

export default CommonCellBuilder;
